/**
 * The group's own companies — the top of the stock structure.
 *
 * Not customers: a company here is one of the legal entities the distributor
 * trades through, and a depot belongs to exactly one of them. Masking one
 * takes it out of every list a desk chooses from and keeps it whole; deleting
 * it takes its depots and their stock with it, after a confirmation that says
 * so.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Button, DataTable, Input, useToast, type Column } from "@hardauto/core-ui";

import type { Company, ReferentialApi, Site } from "../../api";
import { NAMESPACE } from "../../i18n";
import { DeleteDialog, type DeleteTarget } from "./DeleteDialog";
import { PerimeterDialog } from "./PerimeterDialog";
import { VisibilityControls } from "./VisibilityControls";

export interface CompaniesTabProps {
  api: ReferentialApi;
  companies: readonly Company[];
  /** The depots, to say how many a deletion would take. */
  sites?: readonly Site[];
  loading: boolean;
  failed: boolean;
  reload: () => void;
  mayWrite: boolean;
  /** Deciding what a company sells is article data: `referential.article.write`. */
  mayEditPerimeter?: boolean;
}

export function CompaniesTab({
  api,
  companies,
  sites = [],
  loading,
  failed,
  reload,
  mayWrite,
  mayEditPerimeter = false,
}: CompaniesTabProps) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  // The company being renamed, and the name being typed for it.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [perimeterOf, setPerimeterOf] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);

  function askDelete(row: Company) {
    const depots = sites.filter((site) => site.company_code === row.code).length;
    const articles = row.article_count ?? 0;
    setDeleting({
      title: t("deletion.title.company", { name: row.name, code: row.code }),
      consequences: [
        ...(depots > 0 ? [t("deletion.will.depots", { count: depots })] : []),
        ...(articles > 0 ? [t("deletion.will.perimeter", { count: articles })] : []),
      ],
      run: () => api.deleteCompany(row.code),
    });
  }

  async function save(key: string, task: () => Promise<unknown>, after: () => void) {
    await action.run(key, async () => {
      try {
        await task();
        after();
        reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  const columns: Column<Company>[] = [
    {
      key: "code",
      header: t("structure.companies.column.code"),
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "name",
      header: t("structure.companies.column.name"),
      render: (row) =>
        renaming === row.code ? (
          <form
            className="hardauto-structure__inline"
            onSubmit={(event) => {
              event.preventDefault();
              if (!newName.trim()) return;
              void save(
                `rename:${row.code}`,
                () => api.updateCompany(row.code, { name: newName.trim() }),
                () => setRenaming(null),
              );
            }}
          >
            <Input
              aria-label={t("structure.companies.renameLabel", { code: row.code })}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
            <Button
              type="submit"
              size="sm"
              variant="primary"
              busy={action.isBusy(`rename:${row.code}`)}
              busyLabel={t("structure.save")}
            >
              {t("structure.save")}
            </Button>
            <Button size="sm" onClick={() => setRenaming(null)}>
              {t("structure.cancel")}
            </Button>
          </form>
        ) : (
          row.name
        ),
    },
    {
      key: "articles",
      header: t("structure.companies.column.articles"),
      align: "end",
      render: (row) => (
        <div className="hardauto-structure__inline hardauto-structure__inline--end">
          <span className="hardauto-num">{row.article_count ?? 0}</span>
          {mayEditPerimeter ? (
            <Button variant="ghost" size="sm" onClick={() => setPerimeterOf(row)}>
              {t("structure.companies.perimeter")}
            </Button>
          ) : null}
        </div>
      ),
    },
    {
      key: "state",
      header: t("structure.companies.column.state"),
      render: (row) => (
        <div className="hardauto-structure__inline">
          <VisibilityControls
            customers={{ visible: row.is_active, hiddenByParent: false }}
            backOffice={{ visible: row.back_office_visible, hiddenByParent: false }}
            parent="company-or-point"
            editable={mayWrite}
            isBusy={(audience) => action.isBusy(`toggle:${audience}:${row.code}`)}
            onToggle={(audience, visible) =>
              void save(
                `toggle:${audience}:${row.code}`,
                () =>
                  api.updateCompany(
                    row.code,
                    audience === "customers"
                      ? { is_active: visible }
                      : { back_office_visible: visible },
                  ),
                () => undefined,
              )
            }
          />
          {mayWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRenaming(row.code);
                  setNewName(row.name);
                }}
              >
                {t("structure.rename")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => askDelete(row)}>
                {t("deletion.action")}
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="hardauto-structure__panel">
      {mayWrite && !creating ? (
        <div>
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("structure.companies.create")}
          </Button>
        </div>
      ) : null}

      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!code.trim()) return;
            void save(
              "create",
              () => api.createCompany({ code: code.trim(), name: name.trim() }),
              () => {
                setCode("");
                setName("");
                setCreating(false);
              },
            );
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.companies.newCode")} required>
              {(props) => (
                <Input {...props} value={code} onChange={(event) => setCode(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("structure.companies.newName")}>
              {(props) => (
                <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("structure.save")}
            >
              {t("structure.create")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("structure.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <DeleteDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />

      <PerimeterDialog
        api={api}
        company={perimeterOf}
        onClose={() => setPerimeterOf(null)}
        onChanged={reload}
      />

      <DataTable<Company>
        caption={t("structure.companies.caption")}
        columns={columns}
        rows={companies}
        rowKey={(row) => row.code}
        loading={loading}
        loadingLabel={t("structure.companies.loading")}
        error={
          failed
            ? { title: t("error.title"), onRetry: reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{
          title: t("structure.companies.empty.title"),
          description: t("structure.companies.empty.body"),
        }}
      />
    </div>
  );
}
